import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  SignOut, Envelope, ChatCircle, Question, CalendarCheck,
  Plus, Trash, Eye, EyeSlash, PencilSimple, ChartBar
} from "@phosphor-icons/react";

export default function AdminDashboard() {
  const { user, token, logout, BACKEND_URL } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("inquiries");
  const [stats, setStats] = useState(null);

  const api = axios.create({
    baseURL: `${BACKEND_URL}/api`,
    headers: { Authorization: `Bearer ${token}` }
  });

  const loadStats = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  };

  useEffect(() => { loadStats(); }, [tab]);

  const handleLogout = () => { logout(); navigate("/admin/login"); };

  const tabs = [
    { key: "bookings", label: "Bookings", icon: CalendarCheck, count: stats?.upcoming_bookings },
    { key: "inquiries", label: "Inquiries", icon: Envelope, count: stats?.new_inquiries },
    { key: "sources", label: "Sources", icon: ChartBar },
    { key: "testimonials", label: "Testimonials", icon: ChatCircle, count: stats?.total_testimonials },
    { key: "faq", label: "FAQ", icon: Question, count: stats?.total_faq },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505]">
      <header className="bg-[#111111] border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <a href="/" className="font-heading text-xl font-black tracking-tighter text-[#F2EFE9]">
            3<span className="text-[#D4AF37]">SM</span>
          </a>
          <span className="text-xs text-[#8C8C8C] font-body border-l border-white/10 pl-4">Dashboard</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs text-[#8C8C8C] font-body hidden sm:block">{user?.email}</span>
          <button data-testid="admin-logout-btn" onClick={handleLogout} className="text-[#8C8C8C] hover:text-[#D4AF37] transition-colors">
            <SignOut size={20} />
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Total Inquiries", value: stats.total_inquiries },
              { label: "Upcoming Bookings", value: stats.upcoming_bookings },
              { label: "Testimonials", value: stats.total_testimonials },
              { label: "FAQ Items", value: stats.total_faq },
            ].map((s) => (
              <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`} className="bg-[#111111] border border-white/10 p-4 rounded-lg">
                <p className="text-2xl font-heading font-black text-[#D4AF37]">{s.value}</p>
                <p className="text-xs text-[#8C8C8C] font-body mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 mb-8 border-b border-white/10 pb-4 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-body transition-colors whitespace-nowrap ${
                tab === t.key
                  ? 'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30'
                  : 'text-[#8C8C8C] hover:text-[#F2EFE9]'
              }`}
            >
              <t.icon size={16} />
              {t.label}
              {t.count != null && (
                <span className="text-xs bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-0.5 rounded-full">{t.count}</span>
              )}
            </button>
          ))}
        </div>

        {tab === "bookings" && <BookingsPanel api={api} onUpdate={loadStats} />}
        {tab === "inquiries" && <InquiriesPanel api={api} onUpdate={loadStats} />}
        {tab === "sources" && <LeadSourcesPanel api={api} />}
        {tab === "testimonials" && <TestimonialsPanel api={api} onUpdate={loadStats} />}
        {tab === "faq" && <FAQPanel api={api} onUpdate={loadStats} />}
      </div>
    </div>
  );
}

function BookingsPanel({ api, onUpdate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/admin/bookings").then(({ data }) => { setItems(data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  const updateStatus = async (id, status) => {
    await api.put(`/admin/bookings/${id}`, { status });
    load();
    onUpdate();
  };

  const remove = async (id) => {
    await api.delete(`/admin/bookings/${id}`);
    load();
    onUpdate();
  };

  if (loading) return <p className="text-[#8C8C8C] font-body text-sm">Loading...</p>;
  if (!items.length) return <p data-testid="no-bookings" className="text-[#8C8C8C] font-body text-sm">No bookings yet.</p>;

  const statusColors = { confirmed: "text-[#D4AF37]", completed: "text-green-400", cancelled: "text-red-400" };

  return (
    <div className="space-y-3" data-testid="bookings-panel">
      {items.map((b) => (
        <div key={b.id} data-testid={`booking-${b.id}`} className="bg-[#111111] border border-white/10 p-5 rounded-lg">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
            <div>
              <p className="text-[#F2EFE9] font-heading font-semibold text-sm">{b.name}</p>
              <p className="text-xs text-[#8C8C8C] font-body">{b.email}{b.phone && ` · ${b.phone}`}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-bold uppercase tracking-wider ${statusColors[b.status] || 'text-[#8C8C8C]'}`}>{b.status}</span>
              <select data-testid={`booking-status-${b.id}`} value={b.status} onChange={(e) => updateStatus(b.id, e.target.value)}
                className="bg-[#1A1A1A] border border-white/10 rounded text-xs text-[#F2EFE9] px-2 py-1">
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <button data-testid={`booking-delete-${b.id}`} onClick={() => remove(b.id)} className="text-red-400/60 hover:text-red-400 transition-colors">
                <Trash size={16} />
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 text-xs font-body">
            <span className="text-[#D4AF37] uppercase tracking-wider">{b.service_name}</span>
            <span className="text-[#F2EFE9]">{new Date(b.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</span>
            <span className="text-[#F2EFE9]">{b.time_slot}</span>
            <span className="text-[#8C8C8C]">{b.duration} min</span>
            {b.price > 0 && <span className="text-[#D4AF37] font-semibold">${b.price}</span>}
          </div>
          {b.notes && <p className="text-sm text-[#8C8C8C] font-body mt-2">{b.notes}</p>}
          <p className="text-xs text-[#8C8C8C]/50 font-body mt-2">{new Date(b.created_at).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
}

function InquiriesPanel({ api, onUpdate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/admin/inquiries").then(({ data }) => { setItems(data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  const updateStatus = async (id, status) => {
    await api.put(`/admin/inquiries/${id}`, { status });
    load();
    onUpdate();
  };

  const remove = async (id) => {
    await api.delete(`/admin/inquiries/${id}`);
    load();
    onUpdate();
  };

  if (loading) return <p className="text-[#8C8C8C] font-body text-sm">Loading...</p>;
  if (!items.length) return <p data-testid="no-inquiries" className="text-[#8C8C8C] font-body text-sm">No inquiries yet.</p>;

  const statusColors = { new: "text-[#D4AF37]", contacted: "text-blue-400", resolved: "text-green-400" };

  return (
    <div className="space-y-3" data-testid="inquiries-panel">
      {items.map((item) => (
        <div key={item.id} data-testid={`inquiry-${item.id}`} className="bg-[#111111] border border-white/10 p-5 rounded-lg">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
            <div>
              <p className="text-[#F2EFE9] font-heading font-semibold text-sm">{item.name}</p>
              <p className="text-xs text-[#8C8C8C] font-body">{item.email}{item.phone && ` · ${item.phone}`}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-bold uppercase tracking-wider ${statusColors[item.status] || 'text-[#8C8C8C]'}`}>
                {item.status}
              </span>
              <select
                data-testid={`inquiry-status-${item.id}`}
                value={item.status}
                onChange={(e) => updateStatus(item.id, e.target.value)}
                className="bg-[#1A1A1A] border border-white/10 rounded text-xs text-[#F2EFE9] px-2 py-1"
              >
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="resolved">Resolved</option>
              </select>
              <button data-testid={`inquiry-delete-${item.id}`} onClick={() => remove(item.id)} className="text-red-400/60 hover:text-red-400 transition-colors">
                <Trash size={16} />
              </button>
            </div>
          </div>
          <p className="text-xs text-[#D4AF37] font-body mb-2 uppercase tracking-wider">{item.service_type}</p>
          <p className="text-sm text-[#8C8C8C] font-body">{item.message}</p>
          <p className="text-xs text-[#8C8C8C]/50 font-body mt-3">{new Date(item.created_at).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
}

function TestimonialsPanel({ api, onUpdate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", role: "", quote: "", rating: 5 });
  const [editId, setEditId] = useState(null);

  const load = () => {
    api.get("/admin/testimonials").then(({ data }) => { setItems(data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async (e) => {
    e.preventDefault();
    if (editId) {
      await api.put(`/admin/testimonials/${editId}`, form);
    } else {
      await api.post("/admin/testimonials", form);
    }
    setForm({ name: "", role: "", quote: "", rating: 5 });
    setShowForm(false);
    setEditId(null);
    load();
    onUpdate();
  };

  const startEdit = (t) => {
    setForm({ name: t.name, role: t.role, quote: t.quote, rating: t.rating });
    setEditId(t.id);
    setShowForm(true);
  };

  const toggleVisibility = async (t) => {
    await api.put(`/admin/testimonials/${t.id}`, { visible: !t.visible });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/admin/testimonials/${id}`);
    load();
    onUpdate();
  };

  const inputClass = "bg-[#1A1A1A] border border-white/10 rounded-md px-3 py-2 text-[#F2EFE9] placeholder:text-[#8C8C8C]/60 font-body text-sm focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37] outline-none w-full";

  if (loading) return <p className="text-[#8C8C8C] font-body text-sm">Loading...</p>;

  return (
    <div data-testid="testimonials-panel">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-[#8C8C8C] font-body">{items.length} testimonials</p>
        <button
          data-testid="testimonial-add-btn"
          onClick={() => { setShowForm(!showForm); setEditId(null); setForm({ name: "", role: "", quote: "", rating: 5 }); }}
          className="flex items-center gap-2 text-sm text-[#D4AF37] font-bold hover:text-[#E5C158] transition-colors"
        >
          <Plus size={16} /> {showForm ? "Cancel" : "Add New"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="bg-[#111111] border border-[#D4AF37]/30 p-5 rounded-lg mb-4 space-y-3" data-testid="testimonial-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="testimonial-input-name" required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} />
            <input data-testid="testimonial-input-role" required placeholder="Role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputClass} />
          </div>
          <textarea data-testid="testimonial-input-quote" required placeholder="Quote" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} rows={3} className={inputClass} />
          <div className="flex items-center gap-4">
            <label className="text-sm text-[#8C8C8C] font-body">Rating:</label>
            <select data-testid="testimonial-input-rating" value={form.rating} onChange={(e) => setForm({ ...form, rating: parseInt(e.target.value) })} className="bg-[#1A1A1A] border border-white/10 rounded text-sm text-[#F2EFE9] px-2 py-1">
              {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <button data-testid="testimonial-save-btn" type="submit" className="ml-auto bg-[#D4AF37] text-[#050505] font-bold px-6 py-2 rounded-md text-sm uppercase tracking-wider hover:bg-[#E5C158] transition-colors">
              {editId ? "Update" : "Add"}
            </button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {items.map((t) => (
          <div key={t.id} data-testid={`admin-testimonial-${t.id}`} className={`bg-[#111111] border border-white/10 p-4 rounded-lg transition-opacity ${!t.visible ? 'opacity-50' : ''}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <p className="text-[#F2EFE9] font-heading font-semibold text-sm">
                  {t.name} <span className="text-[#D4AF37] font-body font-normal text-xs">-- {t.role}</span>
                </p>
                <p className="text-sm text-[#8C8C8C] font-body mt-1 italic">&ldquo;{t.quote}&rdquo;</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button data-testid={`testimonial-toggle-${t.id}`} onClick={() => toggleVisibility(t)} className="p-1 text-[#8C8C8C] hover:text-[#D4AF37] transition-colors">
                  {t.visible ? <Eye size={16} /> : <EyeSlash size={16} />}
                </button>
                <button data-testid={`testimonial-edit-${t.id}`} onClick={() => startEdit(t)} className="p-1 text-[#8C8C8C] hover:text-[#D4AF37] transition-colors">
                  <PencilSimple size={16} />
                </button>
                <button data-testid={`testimonial-delete-${t.id}`} onClick={() => remove(t.id)} className="p-1 text-red-400/60 hover:text-red-400 transition-colors">
                  <Trash size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FAQPanel({ api, onUpdate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ question: "", answer: "" });
  const [editId, setEditId] = useState(null);

  const load = () => {
    api.get("/admin/faq").then(({ data }) => { setItems(data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async (e) => {
    e.preventDefault();
    if (editId) {
      await api.put(`/admin/faq/${editId}`, form);
    } else {
      await api.post("/admin/faq", form);
    }
    setForm({ question: "", answer: "" });
    setShowForm(false);
    setEditId(null);
    load();
    onUpdate();
  };

  const startEdit = (f) => {
    setForm({ question: f.question, answer: f.answer });
    setEditId(f.id);
    setShowForm(true);
  };

  const toggleVisibility = async (f) => {
    await api.put(`/admin/faq/${f.id}`, { visible: !f.visible });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/admin/faq/${id}`);
    load();
    onUpdate();
  };

  const inputClass = "bg-[#1A1A1A] border border-white/10 rounded-md px-3 py-2 text-[#F2EFE9] placeholder:text-[#8C8C8C]/60 font-body text-sm focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37] outline-none w-full";

  if (loading) return <p className="text-[#8C8C8C] font-body text-sm">Loading...</p>;

  return (
    <div data-testid="faq-panel">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-[#8C8C8C] font-body">{items.length} FAQ items</p>
        <button
          data-testid="faq-add-btn"
          onClick={() => { setShowForm(!showForm); setEditId(null); setForm({ question: "", answer: "" }); }}
          className="flex items-center gap-2 text-sm text-[#D4AF37] font-bold hover:text-[#E5C158] transition-colors"
        >
          <Plus size={16} /> {showForm ? "Cancel" : "Add New"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="bg-[#111111] border border-[#D4AF37]/30 p-5 rounded-lg mb-4 space-y-3" data-testid="faq-form">
          <input data-testid="faq-input-question" required placeholder="Question" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} className={inputClass} />
          <textarea data-testid="faq-input-answer" required placeholder="Answer" value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} rows={3} className={inputClass} />
          <button data-testid="faq-save-btn" type="submit" className="bg-[#D4AF37] text-[#050505] font-bold px-6 py-2 rounded-md text-sm uppercase tracking-wider hover:bg-[#E5C158] transition-colors">
            {editId ? "Update" : "Add"}
          </button>
        </form>
      )}

      <div className="space-y-2">
        {items.map((f, idx) => (
          <div key={f.id} data-testid={`admin-faq-${f.id}`} className={`bg-[#111111] border border-white/10 p-4 rounded-lg transition-opacity ${!f.visible ? 'opacity-50' : ''}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <p className="text-[#F2EFE9] font-heading font-semibold text-sm">{idx + 1}. {f.question}</p>
                <p className="text-sm text-[#8C8C8C] font-body mt-1">{f.answer}</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button data-testid={`faq-toggle-${f.id}`} onClick={() => toggleVisibility(f)} className="p-1 text-[#8C8C8C] hover:text-[#D4AF37] transition-colors">
                  {f.visible ? <Eye size={16} /> : <EyeSlash size={16} />}
                </button>
                <button data-testid={`faq-edit-${f.id}`} onClick={() => startEdit(f)} className="p-1 text-[#8C8C8C] hover:text-[#D4AF37] transition-colors">
                  <PencilSimple size={16} />
                </button>
                <button data-testid={`faq-delete-${f.id}`} onClick={() => remove(f.id)} className="p-1 text-red-400/60 hover:text-red-400 transition-colors">
                  <Trash size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


const SOURCE_LABELS = {
  instagram: "Instagram",
  tiktok: "TikTok",
  google: "Google",
  facebook: "Facebook",
  youtube: "YouTube",
  linkedin: "LinkedIn",
  twitter: "Twitter / X",
  direct: "Direct",
  website: "Website",
  unknown: "Unknown",
};

const SOURCE_COLORS = {
  instagram: "#E1306C",
  tiktok: "#69C9D0",
  google: "#4285F4",
  facebook: "#1877F2",
  youtube: "#FF0000",
  linkedin: "#0A66C2",
  twitter: "#1DA1F2",
  direct: "#8C8C8C",
  website: "#D4AF37",
  unknown: "#5A5A5A",
};

function formatSourceLabel(src) {
  if (!src) return "Unknown";
  return SOURCE_LABELS[src] || src.charAt(0).toUpperCase() + src.slice(1);
}

function LeadSourcesPanel({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/lead-sources")
      .then(({ data }) => { setData(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-[#8C8C8C] font-body text-sm">Loading...</p>;
  if (!data || data.total === 0) {
    return (
      <p data-testid="no-lead-sources" className="text-[#8C8C8C] font-body text-sm">
        No lead-source data yet. Submissions made before tracking went live won't appear here.
      </p>
    );
  }

  const max = data.breakdown[0]?.total || 1;

  return (
    <div data-testid="lead-sources-panel" className="space-y-6">
      <div className="bg-[#111111] border border-white/10 rounded-lg p-5">
        <div className="flex items-baseline justify-between mb-1">
          <h2 className="font-heading text-lg font-bold text-[#F2EFE9]">Where leads come from</h2>
          <span className="text-xs font-body text-[#8C8C8C] uppercase tracking-wider">
            {data.total} total submissions
          </span>
        </div>
        <p className="text-xs text-[#8C8C8C] font-body">
          Aggregated across intake forms, contact inquiries, and bookings. Source is determined from UTM parameters, then HTTP referrer, then defaults to direct.
        </p>
      </div>

      <div className="space-y-3">
        {data.breakdown.map((row) => {
          const pct = (row.total / data.total) * 100;
          const color = SOURCE_COLORS[row.source] || "#D4AF37";
          const width = (row.total / max) * 100;
          return (
            <div
              key={row.source}
              data-testid={`source-row-${row.source}`}
              className="bg-[#111111] border border-white/10 rounded-lg p-4"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  <span className="font-heading text-sm font-semibold text-[#F2EFE9]">
                    {formatSourceLabel(row.source)}
                  </span>
                </div>
                <div className="flex items-baseline gap-3">
                  <span className="text-xs text-[#8C8C8C] font-body tabular-nums">
                    {pct.toFixed(1)}%
                  </span>
                  <span className="font-heading font-black text-lg text-[#D4AF37] tabular-nums">
                    {row.total}
                  </span>
                </div>
              </div>
              <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${width}%`, backgroundColor: color }}
                />
              </div>
              <div className="flex gap-4 mt-2 text-[11px] text-[#8C8C8C] font-body uppercase tracking-wider">
                <span>{row.intakes} intake{row.intakes === 1 ? "" : "s"}</span>
                <span>{row.inquiries} inquir{row.inquiries === 1 ? "y" : "ies"}</span>
                <span>{row.bookings} booking{row.bookings === 1 ? "" : "s"}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
